import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToolbar,
  ToastController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  arrowUndoOutline,
  businessOutline,
  checkmarkOutline,
  chevronDownOutline,
  informationCircleOutline,
  linkOutline,
  logOutOutline,
  notificationsOutline,
  optionsOutline,
  peopleOutline,
  personCircleOutline,
} from 'ionicons/icons';

import { ClientPickerComponent } from '../../core/session/client-picker.component';
import { ImpersonationTarget, Session, SessionService } from '../../core/session/session.service';
import { AccountLoginService } from '../../core/auth/account-login.service';

/**
 * The signed-in user's menu, ported from the Blazor TopMenu's user dropdown: the person's name opens
 * a menu with My Dashboard, the CLIENT PICKER, and Logout.
 *
 * The client picker lives HERE rather than as its own toolbar control, because that is where the
 * Blazor portal puts it ("Client: {name}") and a user switching apps should not have to re-learn the
 * chrome. Switching persists the choice, forces a token refresh (only AuthServer may mint the
 * ActiveClientId claim) and re-fetches the session — so the palette, the menu and the rows all change
 * with no page reload. The Blazor equivalent needs a full navigation.
 *
 * A single-client user sees their client as a label rather than a picker, matching Blazor's
 * CanSwitchClient behaviour.
 */
@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [
    ClientPickerComponent,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPopover,
    IonList,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonModal,
    IonSearchbar,
    IonSpinner,
    IonTitle,
    IonToolbar,
  ],
  template: `
    <ion-button id="user-menu-trigger" fill="clear" [disabled]="switching()">
      @if (switching()) {
        <ion-spinner slot="start" class="h-4 w-4"></ion-spinner>
      } @else {
        <ion-icon slot="start" name="person-circle-outline"></ion-icon>
      }
      {{ displayName() }}
      <ion-icon slot="end" name="chevron-down-outline"></ion-icon>
    </ion-button>

    <!-- The ng-template is required: an inline Ionic overlay relocates its content, which tears the
         @for's embedded views out of the container that owns their context. -->
    <ion-popover trigger="user-menu-trigger" triggerAction="click" [dismissOnSelect]="true">
      <ng-template>
        <ion-list>
          @if (personId(); as id) {
            <ion-item button [detail]="false" (click)="goToDashboard(id)">
              <ion-icon slot="start" name="person-circle-outline"></ion-icon>
              <ion-label>My Dashboard</ion-label>
            </ion-item>
          }

          @if (isImpersonating()) {
            <ion-item button [detail]="false" (click)="stopImpersonating()">
              <ion-icon slot="start" name="arrow-undo-outline"></ion-icon>
              <ion-label>Stop Impersonating</ion-label>
            </ion-item>
          } @else if (canImpersonate()) {
            <ion-item button [detail]="false" (click)="openImpersonate()">
              <ion-icon slot="start" name="people-outline"></ion-icon>
              <ion-label>Impersonate</ion-label>
            </ion-item>
          }

          @if (activeClientName()) {
            <ion-item-divider>
              <ion-label>Client: {{ activeClientName() }}</ion-label>
            </ion-item-divider>

            @if (canSwitch()) {
              @if (manyClients()) {
                <ion-item button [detail]="false" (click)="clientPickerOpen.set(true)">
                  <ion-icon slot="start" name="business-outline"></ion-icon>
                  <ion-label>Switch client…</ion-label>
                </ion-item>
              } @else {
                @for (client of clients(); track client.clientId) {
                  <ion-item button [detail]="false" (click)="switchTo(client.clientId)">
                    <ion-icon slot="start" name="business-outline"></ion-icon>
                    <ion-label>{{ client.clientName }}</ion-label>
                    @if (client.clientId === activeClientId()) {
                      <ion-icon slot="end" name="checkmark-outline" color="primary"></ion-icon>
                    }
                  </ion-item>
                }
              }
            }
          }

          <!-- The user's own sidebar arrangement and startup page. Here rather than under
               Administration because it is a personal preference, not a setting somebody grants. -->
          <ion-item button [detail]="false" (click)="goToMenuSettings()">
            <ion-icon slot="start" name="options-outline"></ion-icon>
            <ion-label>Menu settings</ion-label>
          </ion-item>

          <!-- How this person wants to be told about things. Beside Menu settings because it is the
               same kind of thing — a personal preference, not something an administrator grants. -->
          <ion-item button [detail]="false" (click)="goToNotificationSettings()">
            <ion-icon slot="start" name="notifications-outline"></ion-icon>
            <ion-label>Notification settings</ion-label>
          </ion-item>

          <!-- In-SPA page backed by AuthServer's headless external-logins API — no navigation to the
               AuthServer front-end. (Linking still does an OAuth redirect, but returns to this SPA.) -->
          <ion-item button [detail]="false" (click)="goToExternalLogins()">
            <ion-icon slot="start" name="link-outline"></ion-icon>
            <ion-label>External Logins</ion-label>
          </ion-item>

          @if (canViewSessionInfo()) {
            <ion-item button [detail]="false" (click)="sessionInfoOpen.set(true)">
              <ion-icon slot="start" name="information-circle-outline"></ion-icon>
              <ion-label>Session info</ion-label>
            </ion-item>
          }

          <ion-item button [detail]="false" (click)="logout()">
            <ion-icon slot="start" name="log-out-outline"></ion-icon>
            <ion-label>Logout</ion-label>
          </ion-item>
        </ion-list>
      </ng-template>
    </ion-popover>

    <!-- Client picker, for the same reason the impersonation one below is a modal: a Super Admin is a
         member of every client, so this list runs to several hundred rows and needs search and
         folding that will not fit inside a menu. The menu popover dismisses on select, so it has
         already closed by the time this opens. -->
    <ion-modal [isOpen]="clientPickerOpen()" (didDismiss)="clientPickerOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Switch client</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="clientPickerOpen.set(false)">Cancel</ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <app-client-picker
            [clients]="clients()"
            [activeClientId]="activeClientId()"
            (picked)="switchToFromPicker($event)"
          />
        </ion-content>
      </ng-template>
    </ion-modal>

    <!-- Impersonation picker. A modal (not the popover) because the person list can be long and needs
         a searchbar; the popover dismisses on select, so it's already closed when this opens. -->
    <ion-modal [isOpen]="pickerOpen()" (didDismiss)="pickerOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Impersonate</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="pickerOpen.set(false)">Cancel</ion-button>
            </ion-buttons>
          </ion-toolbar>
          <ion-toolbar>
            <ion-searchbar
              placeholder="Search people"
              [debounce]="150"
              (ionInput)="filter.set($any($event).target.value ?? '')"
            ></ion-searchbar>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          @if (targetsLoading()) {
            <div class="ion-padding ion-text-center">
              <ion-spinner></ion-spinner>
            </div>
          } @else {
            <ion-list>
              @for (target of filteredTargets(); track target.personId) {
                <ion-item button [detail]="false" (click)="startImpersonating(target.personId)">
                  <ion-icon slot="start" name="person-circle-outline"></ion-icon>
                  <ion-label>{{ target.name }}</ion-label>
                </ion-item>
              } @empty {
                <ion-item lines="none">
                  <ion-label class="ion-text-wrap">No people available to impersonate.</ion-label>
                </ion-item>
              }
            </ion-list>
          }
        </ion-content>
      </ng-template>
    </ion-modal>

    <!-- Super-Admin-only session debug panel: the authoritative view of who the API thinks the caller
         is right now (identity, adopted client, impersonation state, and the effective role/permission
         keys), for verifying impersonation actually swapped roles. -->
    <ion-modal [isOpen]="sessionInfoOpen()" (didDismiss)="sessionInfoOpen.set(false)">
      <ng-template>
        <ion-header>
          <ion-toolbar>
            <ion-title>Session info</ion-title>
            <ion-buttons slot="end">
              <ion-button (click)="sessionInfoOpen.set(false)">Close</ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          @if (sessionInfo(); as s) {
            <ion-list>
              <ion-item-divider><ion-label>Identity</ion-label></ion-item-divider>
              <ion-item>
                <ion-label class="ion-text-wrap"
                  >Display name<p>{{ s.displayName || '—' }}</p></ion-label
                >
              </ion-item>
              <ion-item>
                <ion-label class="ion-text-wrap">Person id<p>{{ s.personId ?? '—' }}</p></ion-label>
              </ion-item>
              <ion-item>
                <ion-label class="ion-text-wrap">User id<p>{{ s.userId }}</p></ion-label>
              </ion-item>
              <ion-item>
                <ion-label class="ion-text-wrap"
                  >Super Admin<p>{{ s.isSuperAdmin ? 'yes' : 'no' }}</p></ion-label
                >
              </ion-item>
              <ion-item>
                <ion-label class="ion-text-wrap"
                  >Impersonating<p>{{ s.isImpersonating ? 'yes' : 'no' }}</p></ion-label
                >
              </ion-item>
              @if (s.isImpersonating) {
                <ion-item>
                  <ion-label class="ion-text-wrap"
                    >Signed-in account is Super Admin<p>{{
                      s.isRealSuperAdmin ? 'yes' : 'no'
                    }}</p></ion-label
                  >
                </ion-item>
              }

              <ion-item-divider><ion-label>Active client</ion-label></ion-item-divider>
              <ion-item>
                <ion-label class="ion-text-wrap"
                  >{{ s.activeClientName || '—' }}<p>id: {{ s.activeClientId ?? '—' }}</p></ion-label
                >
              </ion-item>
              <ion-item>
                <ion-label class="ion-text-wrap"
                  >Can switch client<p>{{ s.canSwitchClient ? 'yes' : 'no' }}</p></ion-label
                >
              </ion-item>

              <ion-item-divider>
                <ion-label>Permissions ({{ s.permissions.length }})</ion-label>
              </ion-item-divider>
              @for (perm of s.permissions; track perm) {
                <ion-item>
                  <ion-label class="ion-text-wrap">{{ perm }}</ion-label>
                </ion-item>
              } @empty {
                <ion-item lines="none">
                  <ion-label class="ion-text-wrap">No permissions.</ion-label>
                </ion-item>
              }
            </ion-list>
          }
        </ion-content>
      </ng-template>
    </ion-modal>
  `,
})
export class UserMenuComponent {
  private readonly session = inject(SessionService);
  private readonly oidc = inject(OidcSecurityService);
  private readonly accountLogin = inject(AccountLoginService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastController);

  protected readonly switching = signal(false);

  /** Impersonation picker state. */
  protected readonly pickerOpen = signal(false);
  protected readonly targetsLoading = signal(false);
  private readonly targets = signal<ImpersonationTarget[]>([]);
  protected readonly filter = signal('');
  protected readonly filteredTargets = computed(() => {
    const q = this.filter().trim().toLowerCase();
    const all = this.targets();
    return q ? all.filter((t) => t.name.toLowerCase().includes(q)) : all;
  });

  protected readonly canImpersonate = computed(
    () => this.session.session()?.canImpersonate ?? false,
  );
  protected readonly isImpersonating = computed(
    () => this.session.session()?.isImpersonating ?? false,
  );

  /** Super-Admin-only session debug panel. Gated on the REAL signed-in account's Super Admin status
   * (not the acting one) so it stays reachable while impersonating a non-admin. */
  protected readonly sessionInfoOpen = signal(false);
  protected readonly clientPickerOpen = signal(false);
  protected readonly canViewSessionInfo = computed(
    () => this.session.session()?.isRealSuperAdmin ?? false,
  );
  protected readonly sessionInfo = computed<Session | null>(() => this.session.session());

  protected readonly displayName = computed(
    () => this.session.session()?.displayName || 'Account',
  );
  protected readonly personId = computed(() => this.session.personId());
  protected readonly clients = computed(() => this.session.session()?.accessibleClients ?? []);
  protected readonly activeClientId = computed(() => this.session.session()?.activeClientId ?? null);
  protected readonly activeClientName = computed(
    () => this.session.session()?.activeClientName ?? '',
  );

  /**
   * Whether the client list has outgrown the menu and belongs in the modal picker.
   *
   * A handful of clients is faster to switch inline — one tap, no modal — and that is most users. The
   * threshold is where the list starts to dominate a menu that also holds Dashboard, Impersonate and
   * the rest, not a measured limit.
   */
  protected readonly manyClients = computed(() => this.clients().length > 8);

  /** The server decides this (CanSwitchClient); a one-client user gets a label, not a picker. */
  protected readonly canSwitch = computed(
    () => (this.session.session()?.canSwitchClient ?? false) && this.clients().length > 1,
  );

  constructor() {
    addIcons({
      personCircleOutline,
      chevronDownOutline,
      businessOutline,
      checkmarkOutline,
      logOutOutline,
      peopleOutline,
      arrowUndoOutline,
      linkOutline,
      optionsOutline,
      notificationsOutline,
      informationCircleOutline,
    });
  }

  /**
   * Same destination as the Blazor "My Dashboard": the person's own manage screen. If that person
   * lives in a client other than the active one (common for a Super Admin, who is a member of every
   * client), switch to the person's home client first — otherwise the multi-client query filter hides
   * the row and the page fails to load. Only switch to a client the user can actually act as, so we
   * never trip the server's membership check.
   */
  protected async goToDashboard(personId: number): Promise<void> {
    const homeClientId = this.session.personClientId();
    const canReach = this.clients().some((c) => c.clientId === homeClientId);

    if (homeClientId !== null && homeClientId !== this.activeClientId() && canReach) {
      this.switching.set(true);
      try {
        await this.session.switchClient(homeClientId);
        await this.notifyClientChanged();
      } catch {
        // Fall through and navigate anyway — the page will surface the not-found on its own.
      } finally {
        this.switching.set(false);
      }
    }

    void this.router.navigateByUrl(`/people/manage/${personId}`);
  }

  /** Brief confirmation that the active client was switched for the user (session already reloaded,
   * so activeClientName() is the new one). */
  private async notifyClientChanged(): Promise<void> {
    const toast = await this.toast.create({
      message: `Switched to ${this.activeClientName()}`,
      duration: 2500,
      position: 'bottom',
      color: 'primary',
    });
    await toast.present();
  }

  protected async switchTo(clientId: number): Promise<void> {
    if (clientId === this.activeClientId() || this.switching()) return;

    this.switching.set(true);
    try {
      await this.session.switchClient(clientId);
    } finally {
      this.switching.set(false);
    }
  }

  /** Same switch, but closes the modal first so the app is not re-themed behind an open sheet. */
  protected async switchToFromPicker(clientId: number): Promise<void> {
    this.clientPickerOpen.set(false);
    await this.switchTo(clientId);
  }

  /** Open the picker and (re)load the impersonatable people. */
  protected async openImpersonate(): Promise<void> {
    this.filter.set('');
    this.targets.set([]);
    this.pickerOpen.set(true);
    this.targetsLoading.set(true);
    try {
      this.targets.set(await this.session.getImpersonationTargets());
    } catch {
      await this.showError('Could not load people to impersonate.');
    } finally {
      this.targetsLoading.set(false);
    }
  }

  /** Start impersonating the chosen person; the session reload swaps the menu to "Stop Impersonating". */
  protected async startImpersonating(personId: number): Promise<void> {
    this.pickerOpen.set(false);
    this.switching.set(true);
    try {
      await this.session.impersonate(personId);
      const toast = await this.toast.create({
        message: `Now impersonating ${this.displayName()}`,
        duration: 2500,
        position: 'bottom',
        color: 'primary',
      });
      await toast.present();
    } catch {
      await this.showError('You are not allowed to impersonate that person.');
    } finally {
      this.switching.set(false);
    }
  }

  /** Revert to the real user. */
  protected async stopImpersonating(): Promise<void> {
    this.switching.set(true);
    try {
      await this.session.stopImpersonation();
      const toast = await this.toast.create({
        message: 'Stopped impersonating',
        duration: 2500,
        position: 'bottom',
        color: 'primary',
      });
      await toast.present();
    } finally {
      this.switching.set(false);
    }
  }

  /** In-SPA External Logins page (AuthServer's headless API backs it). */
  protected goToMenuSettings(): void {
    void this.router.navigateByUrl('/account/menu');
  }

  /** The person's own notification channels, per event type — Sprints #133. */
  protected goToNotificationSettings(): void {
    void this.router.navigateByUrl('/account/notifications');
  }

  protected goToExternalLogins(): void {
    void this.router.navigateByUrl('/account/external-logins');
  }

  private async showError(message: string): Promise<void> {
    const toast = await this.toast.create({
      message,
      duration: 3000,
      position: 'bottom',
      color: 'danger',
    });
    await toast.present();
  }

  protected async logout(): Promise<void> {
    // Two independent pieces of "logged in" must both be torn down, without an end-session redirect
    // (which is what triggered the post_logout_redirect_uri ID2052 error):
    //   1. the SPA's own tokens — dropped locally by the oidc client, and
    //   2. the shared AuthServer SSO cookie — cleared via the headless JSON endpoint, so the next
    //      login actually re-prompts instead of silently re-authenticating from the live IdP session.
    await this.accountLogin.logout();
    this.oidc.logoffLocal();
    void this.router.navigateByUrl('/login');
  }
}
